import {
  MAX_PHONE_LENGTH,
  NUMBER_REGEX,
  PHONE_REGEX,
} from "./../types/formConstants";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import InsuranceService from "../Service/service";
import { CustomerFormInfo, IdType } from "../types/form";
import {
  EMAIL_REGEX,
  MIN_NATIONAL_LENGTH,
  validationMessages,
} from "../types/formConstants";
import useBasicModal from "../BasicModal/useBasicModal";
import { FormContext } from "../FormContextProvider";
import { scrollToTop } from "../scrollToTop";

const useInsuranceForm = (nextStep: () => void) => {
  const service = new InsuranceService();
  const [idType, setIdType] = useState(IdType.NATIONAL);
  const [isLoading, setIsLoading] = useState(false);
  const { customerData, setCustomerData } = useContext(FormContext);
  const { show, setShow, setText, text } = useBasicModal();
  const initialValues = customerData;

  const handleSubmit = (values: CustomerFormInfo) => {
    if (formik.isValid && setCustomerData) {
      values.valid = true;
      setCustomerData(values);
      nextStep();
    }
  };

  const validate = (values: any) => {
    const errors: any = {};

    if (values.idType === IdType.NATIONAL) {
      if (values.idNumber.length < MIN_NATIONAL_LENGTH) {
        errors.idNumber = validationMessages.idNumber.length;
      }

      if (!values.idNumber.match(NUMBER_REGEX)) {
        errors.idNumber = validationMessages.idNumber.number;
      }
    }

    if (!values.idNumber) {
      errors.idNumber = validationMessages.idNumber.required;
    }

    if (!values.firstLastName) {
      errors.firstLastName = validationMessages.firstLastName;
    }

    if (!values.name) {
      errors.name = validationMessages.name;
    }

    if (!values.email) {
      errors.email = validationMessages.email.required;
    } else if (!EMAIL_REGEX.test(values.email)) {
      errors.email = validationMessages.email.invalid;
    }

    if (!values.phone) {
      errors.phone = validationMessages.phone.required;
    } else if (!PHONE_REGEX.test(values.phone)) {
      errors.phone = validationMessages.phone.invalid;
    }

    return errors;
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
    validate,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    if (name === "phone") {
      const val = value.toString().slice(0, MAX_PHONE_LENGTH);
      formik.setFieldValue(name, val ? Number(val) : "");
    } else if (name === "idNumber" && idType === IdType.NATIONAL) {
      formik.setFieldValue(
        name,
        value.toString().slice(0, MIN_NATIONAL_LENGTH)
      );
    } else {
      formik.handleChange(event);
    }
  };

  const handleIdTypeChange = (event: any) => {
    const idType = event.target.value;
    formik.setFieldValue("idType", idType);
    setIdType(idType);
  };

  const canShowForm = () => {
    return (
      (idType === IdType.NATIONAL && !isLoading && formik.values.idNumber) ||
      idType !== IdType.NATIONAL
    );
  };

  const handleClose = () => {
    setShow(false);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    setIsLoading(formik.values.idNumber.length > 0);
    if (
      formik.values.idType === IdType.NATIONAL &&
      formik.values.idNumber.length === MIN_NATIONAL_LENGTH
    ) {
      service
        .getPadron(formik.values.idNumber)
        .then((response: any) => {
          if (response.data && !response.data.error) {
            formik.setFieldValue("name", response.data.name);
            formik.setFieldValue("firstLastName", response.data.fLastName);
            formik.setFieldValue("secondLastName", response.data.sLastName);
          } else if (response.data.error) {
            setText(
              "Hubo un error al cargar los datos del padrón\nPor favor digite los datos manualmente"
            );
            setShow(true);
          }
        })
        .catch(() => {
          setText(
            "Hubo un error al cargar los datos del padrón\nPor favor digite los datos manualmente"
          );
          setShow(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.idType, formik.values.idNumber]);

  return {
    handleIdTypeChange,
    handleChange,
    canShowForm,
    handleClose,
    isLoading,
    idType,
    formik,
    text,
    show,
  };
};

export default useInsuranceForm;
